import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { BookableAssetsGridView } from "@/components/BookableAssetsGridView/BookableAssetsGridView";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { BookingOperationalAnalyticsSummary } from "@/components/OperationalAnalytics/OperationalAnalyticsSummaries";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { canManageAnyBookableAsset } from "@/helpers/permissions";
import { useProject } from "@/hooks/Network/useProject";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { getBookingsExportFilename } from "@/modules/bookings/helpers";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { ExportBookingsModal } from "./components/ExportBookingsModal";

export function BookableAssetOverviewPage(): React.ReactNode {
  const slug = useSlug();
  const projectId = useProjectId();
  const { name: projectName } = useProject();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const { t } = useTranslation();
  const [isExportAllBookingsModalOpened, exportAllBookingsModalOpenHandlers] = useBool(false);
  const assetsQuery: ApiQueryParams<"getBookableAssetsV1"> = {
    HideUnpublished: !hasPermission((x) => x.assets.canManageAll || x.assets.canManageOwn),
  };

  const bookableAssetsQueries = useBookableAssetsQueries();
  const {
    data: assetsData,
    isPending: isPendingAssets,
    error: assetsError,
    hasNextPage: hasMoreAssets,
    fetchNextPage: fetchMoreAssets,
    isFetchingNextPage: isFetchingMoreAssets,
  } = useInfiniteQuery(bookableAssetsQueries.listInfinite({ query: assetsQuery }));
  const exportAllBookingsMutation = bookableAssetMutations.useExportAllBookings();

  const allAssets = useMemo(() => assetsData?.pages.flatMap((x) => x.items ?? []) ?? [], [assetsData]);

  if (assetsError) {
    return <ErrorPage error={assetsError} />;
  }
  if (isPendingAssets) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.bookings.list-assets.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.bookings.list-assets.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <Button
              type="link"
              href={`${routes.calendar.list({
                slug,
              })}?tab=reservations`}
              variant="soft"
              icon={<CalendarIcon />}
            >
              {t("page.bookings.list-assets.actions.see-reservations")}
            </Button>
            {hasPermission(canManageAnyBookableAsset) && (
              <Button
                icon={<Download01Icon />}
                variant="soft"
                onClick={exportAllBookingsModalOpenHandlers.setTrue}
                isLoading={exportAllBookingsMutation.isPending}
              >
                {t("page.bookings.list-assets.actions.export")}
              </Button>
            )}
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{ scope: "project", entity: "bookable-assets", page: "list" }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.bookable-assets.suggestion.1"),
                t("component.ask-nobi-chat-drawer.bookable-assets.suggestion.2"),
              ]}
            />
            {hasPermission(canManageAnyBookableAsset) && (
              <Button
                type="link"
                href={routes.bookings.createAsset({
                  slug,
                })}
                variant="filled"
              >
                {t("page.bookings.list-assets.actions.new")}
              </Button>
            )}
          </>
        }
      >
        <Stack gap="md">
          <BookingOperationalAnalyticsSummary />
          <BookableAssetsGridView
            onLoadMore={fetchMoreAssets}
            isLoading={isFetchingMoreAssets}
            hasMore={hasMoreAssets}
            assets={allAssets}
          />
        </Stack>
      </DocumentPage>

      <ExportBookingsModal
        onExport={(includeHistory) =>
          exportAllBookingsMutation.mutate({
            fileName: getBookingsExportFilename(projectName),
            includeHistory,
          })
        }
        isOpened={isExportAllBookingsModalOpened}
        onOpenChange={exportAllBookingsModalOpenHandlers.set}
      />
    </>
  );
}
